import React, { useMemo, useContext, useState } from 'react'
import { useTypedTranslation } from 'app/modules/shared/hooks/use-translation'
import { applicationConfiguration } from '../../../shared'
import OpswatMarketingAlert from '../opswat-marketing-alert'
import ExtendedOptionsBody from './extended-options-body'
import { ScanProfile, ScanProfileOptions } from 'app/modules/user/features/profile/types'
import DropdownSelect from 'app/components/dropdown'
import { capitalize } from '../../helpers'
import { Trans } from 'react-i18next'
import { Link } from 'react-router-dom'
import { getPageUrl } from 'app/modules/shared/routing'
import { ExtendedOption, ScanEngine, ScanEngineLabel } from 'app/modules/reports/components/file-upload-modal/types'

type ExtendedScanOptionsProps = Readonly<{
  data: ScanProfileOptions
  setData: CallableFunction
  disabled: boolean
  scanProfiles: ScanProfile[]
  activeScanProfile: string | undefined
  setActiveScanProfile: CallableFunction
  activeScanEngine: ScanEngine | undefined
  setActiveScanEngine: CallableFunction
  fields: ExtendedOption[]
}>
export function ExtendedScanOptions({
  data,
  setData,
  disabled,
  scanProfiles,
  activeScanProfile,
  setActiveScanProfile,
  activeScanEngine,
  setActiveScanEngine,
  fields,
}: ExtendedScanOptionsProps) {
  const { _t } = useTypedTranslation()
  const config = useContext(applicationConfiguration)
  const customProfileName = _t('custom-options')
  const profilesUrl = getPageUrl('user-profile') + '?active=scan-profiles'
  const [activeScanEngineLabel, setActiveScanEngineLabel] = useState(ScanEngineLabel.Internal)

  const profilesItems = useMemo(() => {
    const result = []
    for (const profile of scanProfiles) {
      result.push({ name: capitalize(profile.name), value: profile.id as string })
    }
    result.push({ name: customProfileName, value: '' })

    return result
  }, [scanProfiles])

  const scanEngines = useMemo(() => {
    const result = []
    for (const engine in ScanEngineLabel) {
      result.push({ name: engine, value: engine })
    }

    return result
  }, [activeScanEngine])

  const activeProfile = useMemo(() => {
    return scanProfiles.find((item: ScanProfile) => item.id === activeScanProfile)
  }, [scanProfiles, activeScanProfile])

  const description = activeProfile ? (activeProfile.description ?? _t('profile-options')) : _t('set-custom-options')

  const activeProfileItem = useMemo(() => {
    const name = profilesItems.find((item) => item.value === activeScanProfile)?.name
    return name ?? customProfileName
  }, [scanProfiles, activeScanProfile])

  function setValue(name: string, value: any) {
    const values: { [key: string]: any } = { ...data }
    values[name] = value

    setData(values as ScanProfileOptions)
  }

  function onOptionChange(name: string) {
    return (value: boolean) => {
      setValue(name, value)
    }
  }

  // This condition is only true on community, where we need to show this notification.
  if (disabled) {
    return <OpswatMarketingAlert />
  }

  return (
    <>
      {config.buildType === 'community' || config.buildType === 'develop' ? (
        <>
          <i className="d-inline-block text-muted mb-3">{_t('scan-engines')}</i>

          <DropdownSelect
            id="scan-engines"
            className="dropdown-no-capitalize"
            onClick={(engine: string) => {
              setActiveScanEngineLabel(engine as ScanEngineLabel)

              engine = engine.toLowerCase() as ScanEngine
              setActiveScanEngine(engine)
            }}
            items={scanEngines}
            value={activeScanEngineLabel}
            disabled={disabled}
          />
        </>
      ) : null}

      <i className="d-inline-block text-muted mb-3 mt-6">
        <Trans i18nKey="generic-options-note">
          Pre-configured analysis options, managed at
          <Link to={profilesUrl} className="text-i">
            Profiles
          </Link>
          page
        </Trans>
      </i>

      <DropdownSelect
        id="scan-profiles"
        className="dropdown-no-capitalize"
        onClick={(profile_id: string) => setActiveScanProfile(profile_id)}
        items={profilesItems}
        value={activeProfileItem}
        disabled={disabled || activeScanEngine !== ScanEngine.Internal}
      />

      <i className="d-inline-block text-muted mb-3 mt-6">{description}</i>

      <ExtendedOptionsBody
        data={activeProfile?.options || data}
        allDisabled={!!activeScanProfile || activeScanEngine !== ScanEngine.Internal}
        onOptionChange={onOptionChange}
        fields={fields}
      />
    </>
  )
}
